if (!customElements.get("stone-quantity-calculator")) {
  customElements.define(
    "stone-quantity-calculator",
    class StoneQuantityCalculator extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.defaultWastage = Math.max(0, Number(this.dataset.defaultWastage) || 10);
        this.currency = this.dataset.currency || "AUD";
        this.variants = this.readVariants();
        this.measurements = this.querySelector("[data-measurements]");
        this.addButton = this.querySelector("[data-add-area]");
        this.wastageToggle = this.querySelector("[data-wastage-toggle]");
        this.useButton = this.querySelector("[data-use-quantity]");
        this.errorElement = this.querySelector("[data-error]");
        this.statusElement = this.querySelector("[data-status]");
        this.coverageNote = this.querySelector("[data-coverage-note]");
        this.section = this.closest("section") || document;
        this.rowIndex = 0;
        this.mode = "area";
        this.activeVariantId = null;
        this.lastCalculation = null;

        this.addButton?.addEventListener("click", () => {
          this.addMeasurementRow();
          this.measurements.querySelector("[data-row]:last-child input")?.focus();
        });
        this.wastageToggle?.addEventListener("change", () => this.calculate());
        this.useButton?.addEventListener("click", () => this.useQuantity());
        this.measurements?.addEventListener("input", (event) => this.onMeasurementInput(event));
        this.measurements?.addEventListener("click", (event) => this.onMeasurementClick(event));

        this.section.addEventListener("variantChangeEnd", () => {
          window.setTimeout(() => this.syncVariant(), 0);
        });
        this.section.addEventListener("change", (event) => {
          if (event.target?.closest?.("variant-options")) {
            window.setTimeout(() => this.syncVariant(), 80);
          }
        });

        this.syncVariant(true);
      }

      readVariants() {
        try {
          return JSON.parse(this.querySelector("[data-stone-variants]")?.textContent || "[]");
        } catch (error) {
          console.error("Stone quantity calculator: invalid variant data", error);
          return [];
        }
      }

      currentVariantId() {
        const formInput = this.section.querySelector(
          '.main-product__buy-buttons--container form[data-type="add-to-cart-form"] input[name="id"], form[data-type="add-to-cart-form"] input[name="id"]',
        );
        const variantOptions = this.section.querySelector("variant-options[data-variant-id]");
        return String(
          formInput?.value ||
            variantOptions?.dataset.variantId ||
            this.variants.find((variant) => variant.selected)?.id ||
            "",
        );
      }

      syncVariant(force = false) {
        const id = this.currentVariantId();
        if (!force && id === this.activeVariantId) return;
        this.activeVariantId = id;
        this.variant = this.variants.find((variant) => String(variant.id) === id) || this.variants[0];
        if (!this.variant) return;

        const previousMode = this.mode;
        this.mode = this.variantMode(this.variant);
        this.coverage = this.variantCoverage(this.variant, this.mode);
        this.querySelector("[data-selected-variant]").textContent = `Selected: ${this.variant.title}`;
        this.statusElement.textContent = "";
        this.errorElement.textContent = "";

        if (force || previousMode !== this.mode || !this.measurements.children.length) {
          this.measurements.innerHTML = "";
          this.rowIndex = 0;
          this.addMeasurementRow();
        }
        this.updateModeLabels();
        this.calculate();
      }

      variantMode(variant) {
        const descriptor = `${variant.title || ""} ${variant.sku || ""}`.toLowerCase();
        return /(corner|per\s*l\.?m\.?|per-lm|bullnose|drop\s*edge|coping|capping)/i.test(descriptor)
          ? "lineal"
          : "area";
      }

      positiveNumber(value) {
        const number = Number(value);
        return Number.isFinite(number) && number > 0 ? number : 0;
      }

      variantCoverage(variant, mode) {
        const descriptor = `${variant.title || ""} ${variant.sku || ""}`.toLowerCase();
        const quantityUnit = String(variant.quantityUnit || "").toLowerCase();
        const referenceUnit = String(variant.referenceUnit || "").toLowerCase();

        if (mode === "lineal") {
          const explicit = this.positiveNumber(variant.variantCoverageLm) || this.positiveNumber(variant.productCoverageLm);
          if (explicit) return explicit;
          if ((quantityUnit === "m" || referenceUnit === "m") && this.positiveNumber(variant.unitQuantity)) {
            return this.positiveNumber(variant.unitQuantity);
          }
          if (/(per\s*l\.?m\.?|per-lm)/i.test(descriptor)) return 1;
          return null;
        }

        const explicit = this.positiveNumber(variant.variantCoverageM2) || this.positiveNumber(variant.productCoverageM2);
        if (explicit) return explicit;
        const squareMetreUnit = [quantityUnit, referenceUnit].some((unit) => /^(m2|m²|square_meters?)$/.test(unit));
        if (squareMetreUnit) return this.positiveNumber(variant.unitQuantity) || 1;
        if (/(per\s*m(?:2|²)|\/\s*m(?:2|²)|per-m2|-m2\b)/i.test(descriptor)) return 1;
        return null;
      }

      addMeasurementRow() {
        this.rowIndex += 1;
        const row = document.createElement("div");
        row.className = "sh-stone-calculator__row";
        row.dataset.row = String(this.rowIndex);
        row.innerHTML = this.mode === "lineal" ? this.linealRowMarkup(this.rowIndex) : this.areaRowMarkup(this.rowIndex);
        this.measurements.appendChild(row);
        this.updateRemoveButtons();
        this.calculate();
      }

      areaRowMarkup(index) {
        return `
          <div class="sh-stone-calculator__field">
            <label for="StoneCalcWidth-${this.dataset.sectionId}-${index}">Area ${index} width (m)</label>
            <input id="StoneCalcWidth-${this.dataset.sectionId}-${index}" type="number" inputmode="decimal" min="0" step="0.01" placeholder="0.00" data-width>
          </div>
          <span class="sh-stone-calculator__times" aria-hidden="true">×</span>
          <div class="sh-stone-calculator__field">
            <label for="StoneCalcHeight-${this.dataset.sectionId}-${index}">Area ${index} height (m)</label>
            <input id="StoneCalcHeight-${this.dataset.sectionId}-${index}" type="number" inputmode="decimal" min="0" step="0.01" placeholder="0.00" data-height>
          </div>
          <button class="sh-stone-calculator__remove" type="button" data-remove-row aria-label="Remove area ${index}">Remove</button>
        `;
      }

      linealRowMarkup(index) {
        return `
          <div class="sh-stone-calculator__field sh-stone-calculator__field--wide">
            <label for="StoneCalcLength-${this.dataset.sectionId}-${index}">Corner or edge ${index} length (m)</label>
            <input id="StoneCalcLength-${this.dataset.sectionId}-${index}" type="number" inputmode="decimal" min="0" step="0.01" placeholder="0.00" data-length>
          </div>
          <button class="sh-stone-calculator__remove" type="button" data-remove-row aria-label="Remove length ${index}">Remove</button>
        `;
      }

      updateRemoveButtons() {
        const rows = [...this.measurements.querySelectorAll("[data-row]")];
        rows.forEach((row) => {
          const remove = row.querySelector("[data-remove-row]");
          if (remove) remove.hidden = rows.length === 1;
        });
      }

      onMeasurementInput(event) {
        if (!event.target.matches("input[type='number']")) return;
        if (Number(event.target.value) < 0) event.target.value = "0";
        event.target.removeAttribute("aria-invalid");
        this.calculate();
      }

      onMeasurementClick(event) {
        const remove = event.target.closest("[data-remove-row]");
        if (!remove) return;
        remove.closest("[data-row]")?.remove();
        this.updateRemoveButtons();
        this.calculate();
      }

      updateModeLabels() {
        const unit = this.mode === "lineal" ? "lm" : "m²";
        this.querySelector("[data-measured-label]").textContent = this.mode === "lineal" ? "Measured length:" : "Measured area:";
        this.querySelector("[data-add-area-label]").textContent = this.mode === "lineal" ? "Add another length" : "Add another area";
        this.querySelector("[data-wastage-label]").textContent = this.defaultWastage;
        this.querySelector("[data-wastage-result-label]").textContent = `${this.defaultWastage}% wastage:`;
        this.querySelector("[data-measured-value]").textContent = `0.00 ${unit}`;
        this.querySelector("[data-wastage-value]").textContent = `0.00 ${unit}`;
        this.querySelector("[data-recommended-value]").textContent = `0.00 ${unit}`;
      }

      readMeasurements() {
        let total = 0;
        let invalid = false;
        this.measurements.querySelectorAll("[data-row]").forEach((row) => {
          if (this.mode === "lineal") {
            total += this.positiveNumber(row.querySelector("[data-length]")?.value);
            return;
          }
          const width = this.positiveNumber(row.querySelector("[data-width]")?.value);
          const height = this.positiveNumber(row.querySelector("[data-height]")?.value);
          if ((width > 0 && height === 0) || (height > 0 && width === 0)) {
            invalid = true;
            row.querySelectorAll("input").forEach((input) => input.setAttribute("aria-invalid", "true"));
          }
          total += width * height;
        });
        return { total, invalid };
      }

      calculate() {
        if (!this.variant) return;
        const measurement = this.readMeasurements();
        const wastage = this.wastageToggle?.checked ? measurement.total * (this.defaultWastage / 100) : 0;
        const required = measurement.total + wastage;
        const hasCoverage = Number.isFinite(this.coverage) && this.coverage > 0;
        const units = hasCoverage && required > 0 ? Math.ceil(required / this.coverage - 1e-9) : 0;
        const recommended = hasCoverage && units > 0 ? units * this.coverage : required;
        const unit = this.mode === "lineal" ? "lm" : "m²";
        const price = this.positiveNumber(this.variant.price) / 100;

        this.querySelector("[data-measured-value]").textContent = `${measurement.total.toFixed(2)} ${unit}`;
        this.querySelector("[data-wastage-value]").textContent = `${wastage.toFixed(2)} ${unit}`;
        this.querySelector("[data-recommended-value]").textContent = `${recommended.toFixed(2)} ${unit}`;
        this.querySelector("[data-wastage-result-label]").textContent = this.wastageToggle?.checked
          ? `${this.defaultWastage}% wastage:`
          : "Wastage allowance:";

        if (hasCoverage) {
          this.querySelector("[data-cost-value]").textContent = this.money(units * price);
          this.coverageNote.hidden = true;
          this.coverageNote.textContent = "";
        } else {
          this.querySelector("[data-cost-value]").textContent = `${this.money(price)} per cart unit`;
          this.coverageNote.hidden = false;
          this.coverageNote.textContent =
            "A confirmed pack or cart-unit coverage is not available for this variant, so the required measurement is shown without setting an inaccurate cart quantity.";
        }

        this.errorElement.textContent = measurement.invalid ? "Enter both a width and height for each area." : "";
        this.useButton.disabled = measurement.invalid || required <= 0 || !hasCoverage;
        this.lastCalculation = { units, required, recommended, hasCoverage, invalid: measurement.invalid };
      }

      useQuantity() {
        if (!this.lastCalculation?.hasCoverage || this.lastCalculation.invalid || this.lastCalculation.units < 1) return;
        const quantityInput = this.section.querySelector(
          '.main-product__buy-buttons--container form[data-type="add-to-cart-form"] input[name="quantity"], form[data-type="add-to-cart-form"] input[name="quantity"]',
        );
        if (!quantityInput) {
          this.errorElement.textContent = "The product quantity field could not be found. Please enter the quantity manually.";
          return;
        }
        const maximum = Number(quantityInput.max || 0);
        if (maximum > 0 && this.lastCalculation.units > maximum) {
          this.errorElement.textContent = `This quantity is above the online limit of ${maximum}. Please contact Stone Haus Co for assistance.`;
          return;
        }
        quantityInput.value = String(this.lastCalculation.units);
        quantityInput.dispatchEvent(new Event("input", { bubbles: true }));
        quantityInput.dispatchEvent(new Event("change", { bubbles: true }));
        this.errorElement.textContent = "";
        this.statusElement.textContent = `Product quantity updated to ${this.lastCalculation.units}.`;
        quantityInput.focus({ preventScroll: true });
      }

      money(amount) {
        return new Intl.NumberFormat("en-AU", {
          style: "currency",
          currency: this.currency,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(amount || 0);
      }
    },
  );
}
